import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Trash2, Database, Clock, User, MessageSquare, Target, Activity, Bell, Smartphone, Settings, ExternalLink, BarChart3 } from 'lucide-react';
import { storage } from '../../services/storage';

import { API_BASE_URL } from '../../utils/api'; // Centralized environment-aware API URL

interface UserDataSummary {
  user_info: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    created_at: string;
    auth_method: string;
    status: string;
    last_active_at?: string;
  };
  data_counts: {
    memory_records: number;
    chat_messages: number;
    goals_plans_workouts: number;
    activities: number;
    devices: number;
  };
  estimated_total_records: number;
}

interface UserDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  userName: string;
  onDeletionComplete: () => void;
}

export const UserDeletionModal: React.FC<UserDeletionModalProps> = ({
  isOpen,
  onClose,
  userId,
  userEmail,
  userName,
  onDeletionComplete
}) => {
  const [step, setStep] = useState<'summary' | 'confirm' | 'deleting' | 'complete'>('summary');
  const [dataSummary, setDataSummary] = useState<UserDataSummary | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [reason, setReason] = useState('Admin deletion for testing');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletionResults, setDeletionResults] = useState<any>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('summary');
      setDataSummary(null);
      setConfirmationText('');
      setReason('Admin deletion for testing');
      setError(null);
      setDeletionResults(null);
      loadDataSummary();
    }
  }, [isOpen, userId]);

  const loadDataSummary = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      
      const response = await fetch(`${API_BASE}/users/${userId}/data-summary`, {
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDataSummary(data.summary);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to load user data summary');
      }
    } catch (error) {
      console.error('Failed to load data summary:', error);
      setError('Failed to load user data summary');
    } finally {
      setLoading(false);
    }
  };

  // Poll the background deletion until the user is gone (or it fails).
  // The cascade runs server-side as a BackgroundTask, so completion is detected
  // by the user row disappearing rather than by the POST response.
  const pollDeletionStatus = async (): Promise<void> => {
    const API_BASE = window.location.hostname === 'localhost' ?
      '/api/admin' : `${API_BASE_URL}/api/admin`;
    const token = await storage.getItem('admin_token') || 'admin-token';

    const maxAttempts = 120; // 120 × 3s ≈ 6 minutes
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      try {
        const res = await fetch(`${API_BASE}/users/${userId}/deletion-status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
        if (!res.ok) continue; // transient (e.g. brief 5xx) — keep polling

        const data = await res.json();
        if (data.status === 'completed') {
          setDeletionResults({ user_name: userName });
          setStep('complete');
          setTimeout(() => {
            onDeletionComplete();
          }, 2000);
          return;
        }
        if (data.status === 'failed') {
          setError(data.error || 'Deletion failed during background processing');
          setStep('confirm');
          return;
        }
        // status === 'in_progress' → keep polling
      } catch (err) {
        // transient network error — keep polling
      }
    }

    // Timed out waiting. The deletion may still complete server-side.
    setError(
      'Deletion is still running in the background. It will finish shortly — ' +
      'close this dialog and refresh the user list in a minute to confirm.'
    );
    setStep('confirm');
    onDeletionComplete();
  };

  const handleDeleteUser = async () => {
    if (confirmationText !== 'DELETE ALL USER DATA') {
      setError('Please type the exact confirmation text');
      return;
    }

    setStep('deleting');
    setError(null);

    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      
      const response = await fetch(`${API_BASE}/users/${userId}/delete-completely`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: userId,
          confirmationText: confirmationText,
          reason: reason
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'started') {
          // New async path: the heavy cascade runs server-side as a background
          // task (so it can't hit the ALB's 60s idle timeout, which the browser
          // misreports as a CORS error). Poll until the user is gone.
          await pollDeletionStatus();
        } else {
          // Legacy synchronous path (backend not yet on the async build).
          setDeletionResults(data.deletion_results);
          setStep('complete');
          setTimeout(() => {
            onDeletionComplete();
          }, 2000);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete user');
        setStep('confirm');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError('Failed to delete user');
      setStep('confirm');
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDataCategoryIcon = (category: string) => {
    switch (category) {
      case 'memory_records': return <Database className="w-4 h-4" />;
      case 'chat_messages': return <MessageSquare className="w-4 h-4" />;
      case 'goals_plans_workouts': return <Target className="w-4 h-4" />;
      case 'activities': return <Activity className="w-4 h-4" />;
      case 'devices': return <Smartphone className="w-4 h-4" />;
      default: return <BarChart3 className="w-4 h-4" />;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'memory_records': return 'Memory Records';
      case 'chat_messages': return 'Chat Messages';
      case 'goals_plans_workouts': return 'Goals, Plans & Workouts';
      case 'activities': return 'Activities & Integrations';
      case 'devices': return 'Devices & Security';
      default: return category;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Delete User Data</h2>
              <p className="text-sm text-gray-500">
                {step === 'summary' && 'Review data to be deleted'}
                {step === 'confirm' && 'Confirm permanent deletion'}
                {step === 'deleting' && 'Deleting user data...'}
                {step === 'complete' && 'Deletion completed'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={step === 'deleting'}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3 mb-3">
              <User className="w-5 h-5 text-gray-600" />
              <h3 className="font-medium text-gray-900">User Information</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Name:</span>
                <span className="ml-2 font-medium">{userName}</span>
              </div>
              <div>
                <span className="text-gray-500">Email:</span>
                <span className="ml-2 font-medium">{userEmail}</span>
              </div>
              {dataSummary && (
                <>
                  <div>
                    <span className="text-gray-500">Auth Method:</span>
                    <span className="ml-2 font-medium capitalize">{dataSummary.user_info.auth_method}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className="ml-2 font-medium capitalize">{dataSummary.user_info.status}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <span className="ml-2 font-medium">{formatDate(dataSummary.user_info.created_at)}</span>
                  </div>
                  {dataSummary.user_info.last_active_at && (
                    <div>
                      <span className="text-gray-500">Last Active:</span>
                      <span className="ml-2 font-medium">{formatDate(dataSummary.user_info.last_active_at)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Step Content */}
          {step === 'summary' && (
            <div className="space-y-6">
              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading user data summary...</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="text-red-800 font-medium">Error</span>
                  </div>
                  <p className="text-red-700 mt-1">{error}</p>
                </div>
              )}

              {dataSummary && (
                <div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <span className="text-red-800 font-medium">Warning: Permanent Deletion</span>
                    </div>
                    <p className="text-red-700 text-sm">
                      This action will permanently delete all user data and cannot be undone. 
                      The user will be able to log in again as if they were a completely new user.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                      <Database className="w-5 h-5" />
                      <span>Data to be Deleted</span>
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                        {dataSummary.estimated_total_records} total records
                      </span>
                    </h4>

                    <div className="grid grid-cols-1 gap-3">
                      {Object.entries(dataSummary.data_counts).map(([category, count]) => (
                        <div key={category} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            {getDataCategoryIcon(category)}
                            <span className="font-medium text-gray-900">{getCategoryLabel(category)}</span>
                          </div>
                          <span className="bg-gray-200 text-gray-800 text-sm px-2 py-1 rounded-full">
                            {count} records
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h5 className="font-medium text-yellow-800 mb-2">What will be deleted:</h5>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        <li>• All chat messages and conversation history</li>
                        <li>• All memories, notes, and AI-generated insights</li>
                        <li>• All goals, plans, and workout data</li>
                        <li>• All activity data and external integrations</li>
                        <li>• All profile information and preferences</li>
                        <li>• All device bindings and security data</li>
                        <li>• All notification and interaction history</li>
                        <li>• The user account itself</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800 font-medium">Final Confirmation Required</span>
                </div>
                <p className="text-red-700 text-sm">
                  You are about to permanently delete all data for <strong>{userName} ({userEmail})</strong>.
                  This action cannot be undone.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for deletion:
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="e.g., Admin deletion for testing"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <code className="bg-gray-100 px-2 py-1 rounded text-red-600 font-mono">DELETE ALL USER DATA</code> to confirm:
                  </label>
                  <input
                    type="text"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono"
                    placeholder="DELETE ALL USER DATA"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <span className="text-red-800 font-medium">Error</span>
                    </div>
                    <p className="text-red-700 mt-1">{error}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 'deleting' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Deleting User Data</h3>
              <p className="text-gray-600">
                This can take a few minutes for accounts with a lot of history.
              </p>
              <div className="mt-4 text-sm text-gray-500">
                <p>Keep this window open to see the result — the deletion runs on
                the server and will finish even if you navigate away.</p>
              </div>
            </div>
          )}

          {step === 'complete' && deletionResults && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Deletion Complete</h3>
              <p className="text-gray-600 mb-4">
                All data for {deletionResults.user_name} has been permanently deleted.
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                <h4 className="font-medium text-green-800 mb-2">Deletion Summary:</h4>
                <div className="text-sm text-green-700 space-y-1">
                  {deletionResults.total_records_deleted !== undefined && (
                    <p>• Total records deleted: <strong>{deletionResults.total_records_deleted}</strong></p>
                  )}
                  <p>• User can now log in as a completely new user</p>
                  <p>• Deletion logged for audit trail</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          {step === 'summary' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep('confirm')}
                disabled={loading || !dataSummary}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Proceed to Delete
              </button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <button
                onClick={() => setStep('summary')}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={confirmationText !== 'DELETE ALL USER DATA'}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete All User Data</span>
              </button>
            </>
          )}

          {step === 'deleting' && (
            <div className="w-full text-center">
              <p className="text-gray-600">Deletion in progress...</p>
            </div>
          )}

          {step === 'complete' && (
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
